import { makeStyles } from '@material-ui/core/styles'
import { Autocomplete, Box, Button, Grid, Stack, TextField } from '@mui/material'
import { ContractType, LegalEntityParty, Terms } from '@types'
import {
  commonNoActionButtonStyle,
  commonProceedButtonStyle
} from 'components/request-action/styles/styles'
import { GlobalInfoContext, initialState } from 'contexts/GlobalStateContext'
import { CreateGxInitialValues } from 'contexts/infer-types/create-initial'
import { Dayjs } from 'dayjs'
import { useFormik } from 'formik'
import Item from 'pages/dashboard/sub-sections/items'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { clearSponsoredLegalEntity } from 'redux/slices/sponsored-private-entity'
import { PATH_DASHBOARD } from 'routes/paths'
import * as Yup from 'yup'
import { RootState, dispatch, useSelector } from '../../../../redux/store'
import { AmountAndCurrency } from '../AmountAndCurrency'
import { EntityDetailsRow } from '../EntityRow'
import { ExpiryDate, TimeRow } from '../section'
import { ElementBreak, Heading, SectionBreak } from './shared'
import { convertAmountStringToObject, defineAmountState } from './utils'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    width: '100%',
    paddingBottom: '2rem',
    justifyContent: 'space-between',
    textAlign: 'right',
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: 20
  }
})

export type InitialValues = CreateGxInitialValues & {
  name: string
  startDate: string
  endDate: string
}

type PurposeType = {
  label: string
  type: string
}
const PaperGuarantee = () => {
  const classes = useStyles()
  const navigate = useNavigate()

  const [, setApplicant] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })
  const [n, setBene] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })
  const [, setIssuer] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })

  const [, setCurrency] = useState('AUD')
  const [purposeTypeDes, setPurposeTypeDes] = useState<PurposeType>({ label: '', type: '' })
  const [startDateValue, setstartDateValue] = useState<Dayjs | null>(null)
  const [endDateValue, setendDateValue] = useState<Dayjs | null>(null)
  const [contractExpiryValue, setContractExpiryValue] = useState<Dayjs | null>(null)

  const {
    setReqInfo,
    purpose,
    applicants,
    beneficiaries,
    issuers,
    reference,
    expiryDate,
    isOpenEnded,
    type,
    purposeTypeDescription,
    amount: contextAmount,
    terms: contextTerms
  } = useContext(GlobalInfoContext)
  const initiatedBy = location.pathname.includes('applicant') ? 'applicant' : 'beneficiary'

  const [amountSet, setAmount] = useState(defineAmountState(contextAmount))
  const [currentActiveType, setCurrentActiveType] = useState('')
  const [terms, setTerms] = useState<Terms>(contextTerms)
  const [isTermsSelected, setIsTermsSelected] = useState(false)
  const [isTermsClicked, setIsTermsClicked] = useState(false)
  const { isLoading: isTermsPdfLoading } = useSelector((state: RootState) => state.pdfReducer)
  useEffect(() => {
    setTerms(contextTerms)
  }, [contextTerms?.termsDisplay])

  const [, setStartYear] = useState('')
  const [, setStartMonth] = useState('')
  const [, setStartDay] = useState('')
  const [, setContractExpiryYear] = useState('')
  const [, setContractExpiryMonth] = useState('')
  const [, setContractExpiryDay] = useState('')
  const { businessUnitActionContexts: buacs } = useSelector(
    (state: RootState) => state.createGuaranteePayload
  )
  const paperBuacs = buacs.filter((b) => b.contractType === ContractType.TriContractPaperRecord)
  const GuaranteeCreateSchema = Yup.object().shape({
    applicants: Yup.array()
      .of(
        Yup.object().shape({
          legalEntityIdentifier: Yup.string().notRequired(),
          designatedPointOfContact: Yup.object()
            .shape({
              position: Yup.string(),
              person: Yup.object().shape({
                firstName: Yup.string(),
                lastName: Yup.string(),
                phone: Yup.string(),
                email: Yup.string().email()
              })
            })
            .notRequired()
        })
      )
      .nullable()
      .required('At least one Applicant is required'),
    beneficiaries: Yup.array()
      .of(
        Yup.object().shape({
          legalEntityIdentifier: Yup.string().notRequired(),
          designatedPointOfContact: Yup.object()
            .shape({
              position: Yup.string(),
              person: Yup.object().shape({
                firstName: Yup.string(),
                lastName: Yup.string(),
                phone: Yup.string(),
                email: Yup.string().email()
              })
            })
            .notRequired()
        })
      )
      .nullable()
      .required('At least one Beneficiary is required'),
    issuers: Yup.string().when({
      is: () => initiatedBy === 'beneficiary',
      then: Yup.string().notRequired(),
      otherwise: Yup.string().required('Issuer Name is required')
    }),
    purposeTypeDescription: Yup.string().trim().required('Purpose Type description is required'),
    purposeDescription: Yup.string().trim().min(3).required('Purpose of the guarantee is required'),
    amount: Yup.string().min(1).required('Amount cannot be empty'),
    number: Yup.string().min(3).notRequired(),
    name: Yup.string().min(3).notRequired(),
    comments: Yup.string().trim().min(3).notRequired(),
    startDate: Yup.date().notRequired(),
    endDate: Yup.date().notRequired(),
    terms: Yup.string().notRequired(),
    isOpenEnded: Yup.boolean().notRequired(),
    expiryDate: Yup.date().when(['isOpenEnded'], {
      is: (isOpenEnded: boolean) => isOpenEnded,
      then: Yup.date().notRequired(),
      otherwise: Yup.date().required('Expiry Date is required')
    }),
    reference: Yup.string().notRequired()
  })

  const [, setendDateYear] = useState('')
  const [, setendDateMonth] = useState('')
  const [, setendDateDay] = useState('')

  const formik = useFormik<InitialValues>({
    initialValues: {
      applicants: applicants[0]?.commonName || '',
      beneficiaries: [
        {
          legalEntityIdentifier: '',
          onboarded: false,
          designatedPointOfContact: {
            position: '',
            person: {
              firstName: '',
              lastName: '',
              phone: '',
              email: ''
            }
          }
        }
      ],
      issuers: issuers[0]?.commonName || '',
      purposeTypeDescription: purposeTypeDescription || '',
      terms: terms.termsDisplay || '',
      purposeDescription: purpose?.purposeDescription || '',
      number: purpose?.contract?.number || '',
      name: purpose?.contract?.name || '',
      comments: purpose?.contract?.comments || '',
      expiryDate: expiryDate || '',
      startDate: purpose?.contract?.startDate || '',
      endDate: purpose?.contract?.endDate || '',
      amount: defineAmountState(contextAmount),
      isOpenEnded: isOpenEnded || false,
      reference: reference || ''
    },
    validationSchema: GuaranteeCreateSchema,
    onSubmit: (values) => {
      setReqInfo({
        reference: values?.reference,
        terms,
        purposeTypeDescription: values?.purposeTypeDescription,
        amount: convertAmountStringToObject(amountSet),
        expiryDate: values?.expiryDate,
        isOpenEnded: values?.isOpenEnded,
        purpose: {
          ...purpose,
          purposeDescription: values?.purposeDescription,
          contract: {
            ...purpose?.contract,
            name: values?.name,
            number: values?.number,
            comments: values?.comments
          }
        }
      })
      navigate(PATH_DASHBOARD.general.guaranteeCreate.paperReview)
    }
  })
  const { errors, touched, getFieldProps } = formik

  const handleAmount = (value: string) => {
    formik.setFieldValue('amount', value, true)
    setAmount(value)
  }
  /*Clear sponsored Entity*/
  useEffect(() => {
    dispatch(clearSponsoredLegalEntity())
  }, [dispatch])
  const childRef = useRef()
  const childRefForTerms = useRef()
  const issuerRef = useRef()
  const handleDate = (value: Dayjs, type: string) => {
    const formattedValue = value?.format('YYYY/MM/DD')

    if (type === 'expiryDate') {
      formik.setFieldValue('expiryDate', formattedValue, true)
      setContractExpiryValue(value)
      setContractExpiryYear(formattedValue?.split('/')[0])
      setContractExpiryMonth(formattedValue?.split('/')[1])
      setContractExpiryDay(formattedValue?.split('/')[2])
    } else if (type === 'startDate') {
      formik.setFieldValue('startDate', formattedValue, true)

      setstartDateValue(value)
      setStartYear(formattedValue?.split('/')[0])
      setStartMonth(formattedValue?.split('/')[1])
      setStartDay(formattedValue?.split('/')[2])
    } else if (type === 'endDate') {
      formik.setFieldValue('endDate', formattedValue, true)

      setendDateValue(value)
      setendDateYear(formattedValue?.split('/')[0])
      setendDateMonth(formattedValue?.split('/')[1])
      setendDateDay(formattedValue?.split('/')[2])
    }
    if (type === 'expiryDate') {
      setReqInfo({
        expiryDate: formattedValue,
        purpose: {
          ...purpose,
          contract: {
            ...purpose?.contract
          }
        }
      })
    } else {
      setReqInfo({
        purpose: {
          ...purpose,
          contract: {
            ...purpose?.contract,
            [`${type}`]: formattedValue
          }
        }
      })
    }
  }

  const handleOpenEndedDate = (value) => {
    setReqInfo({
      isOpenEnded: !isOpenEnded,
      expiryDate: null,
      purpose: {
        ...purpose,
        contract: {
          ...purpose?.contract
        }
      }
    })
    value && formik.setFieldValue('expiryDate', '')
    setContractExpiryValue(null)
    formik.setFieldValue('isOpenEnded', value)
  }

  const handlePurposeTypeDescription = (value: PurposeType) => {
    formik.setFieldValue('purposeTypeDescription', value ? value?.type : '')
  }
  const purposePaperDescription = [
    { label: 'Payment Guarantee', type: 'PAYMENTGX' },
    { label: 'Commercial Guarantee', type: 'COMMERCIALGX' },
    { label: 'Performance Guarantee', type: 'PERFORMANCEGX' },
    { label: 'Other Performance Guarantee', type: 'OTHERPERFORMANCE' },
    { label: 'Other Financial Guarantee', type: 'OTHERFINANCIAL' }
  ]
  const handleEntityValue = useCallback(
    (value: LegalEntityParty, type: string) => {
      if (typeof value === 'object') {
        if (type === 'applicant') {
          setCurrentActiveType('applicant')
          formik.setFieldValue('applicants', value?.commonName, true)

          setApplicant(value)
          setReqInfo({
            applicants: [value]
          })
        } else if (type === 'beneficiary') {
          setCurrentActiveType('beneficiaries')
          formik.setFieldValue('beneficiaries', value?.commonName, true)
          setBene(value)
          setReqInfo({
            beneficiaries: [value]
          })
        } else if (type === 'issuer') {
          setCurrentActiveType('issuers')
          formik.setFieldValue('issuers', value?.commonName, true)

          setIssuer(value)
          setReqInfo({
            issuers: [value]
          })
        }
      }
      dispatch(clearSponsoredLegalEntity())
    },
    [formik, setCurrentActiveType, setReqInfo, dispatch]
  )

  const handleClear = useCallback(
    (type: string) => {
      if (type === 'applicant') {
        formik.setFieldValue('applicant', '', true)
        setReqInfo({
          applicants: []
        })
      } else if (type === 'bene') {
        formik.setFieldValue('beneficiaries', '', true)
        setReqInfo({
          beneficiary: []
        })
      } else if (type === 'issuer') {
        formik.setFieldValue('issuers', '', true)

        setReqInfo({
          issuers: []
        })
      }
    },
    [formik, setReqInfo]
  )

  const exitCreationProcess = () => {
    navigate(PATH_DASHBOARD.general.app)
    setReqInfo(initialState)
  }

  const initialApplicantOption = useMemo(() => applicants[0], [applicants])
  const initialBeneficiaryOption = useMemo(() => beneficiaries[0], [beneficiaries])
  const initialIssuerOption = useMemo(() => issuers[0], [issuers])
  const memoizedInitialValues = useMemo(() => paperBuacs, [paperBuacs])
  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <SectionBreak>
          <Grid container sx={{ marginBottom: '20px' }}>
            <Heading>Parties</Heading>
          </Grid>
          {initiatedBy === 'applicant' ? (
            <div>
              <ElementBreak>
                <EntityDetailsRow
                  label="Applicant Name"
                  type="applicant"
                  touched={touched}
                  key={`applicant-${type}`}
                  errors={errors}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'applicant')}
                  initialOption={initialApplicantOption}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={memoizedInitialValues}
                  ref={childRef}
                  resetIssuer={issuerRef}
                  clearable={false}
                />
              </ElementBreak>
              <ElementBreak>
                <EntityDetailsRow
                  label="Beneficiary Name"
                  type="bene"
                  touched={touched}
                  errors={errors}
                  key={`bene-${type}`}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'beneficiary')}
                  initialOption={initialBeneficiaryOption}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={[]}
                  resetTermsRef={childRefForTerms}
                  ref={childRef}
                  resetIssuer={issuerRef}
                />
              </ElementBreak>
            </div>
          ) : (
            <div>
              <ElementBreak>
                <EntityDetailsRow
                  label="Beneficiary Name"
                  type="bene"
                  touched={touched}
                  errors={errors}
                  key={`bene-${type}`}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'beneficiary')}
                  initialOption={initialBeneficiaryOption}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={memoizedInitialValues}
                  clearable={false}
                />
              </ElementBreak>
              <ElementBreak>
                <EntityDetailsRow
                  label="Applicant Name"
                  type="applicant"
                  touched={touched}
                  errors={errors}
                  key={`applicant-${type}`}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'applicant')}
                  initialOption={initialApplicantOption}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={[]}
                  resetTermsRef={childRefForTerms}
                  ref={childRef}
                />
              </ElementBreak>
            </div>
          )}

          <ElementBreak>
            <EntityDetailsRow
              label="Issuer Name"
              type="issuer"
              touched={touched}
              errors={errors}
              getFieldProps={getFieldProps}
              key={`issuer-${type}`}
              setValue={(value) => handleEntityValue(value, 'issuer')}
              initialOption={initialIssuerOption}
              handleClear={handleClear}
              initialValues={memoizedInitialValues}
              initiatedBy={initiatedBy}
              resetTermsRef={childRefForTerms}
              issuerRef={issuerRef}
              ref={childRef}
            />
          </ElementBreak>
        </SectionBreak>
        <SectionBreak>
          <Grid container sx={{ marginBottom: '20px' }}>
            <Heading>Guarantee Purpose Type</Heading>
          </Grid>
          <ElementBreak>
            <Grid item xs={3.9}>
              <Item>
                <Autocomplete
                  disablePortal
                  id="purpose-paper-type-description"
                  options={purposePaperDescription}
                  getOptionLabel={(options: PurposeType) => (options?.label ? options?.label : '')}
                  onChange={(event: any, option: PurposeType, reason: string) => {
                    handlePurposeTypeDescription(option)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="text"
                      {...getFieldProps('purposeTypeDescription')}
                      autoComplete="off"
                      placeholder="Select Purpose Type"
                      InputLabelProps={{ shrink: true }}
                      label="Purpose Type Description"
                      value={formik.values.purposeTypeDescription}
                      error={Boolean(
                        touched.purposeTypeDescription && errors.purposeTypeDescription
                      )}
                      helperText={touched.purposeTypeDescription && errors.purposeTypeDescription}
                    />
                  )}
                />
              </Item>
            </Grid>
          </ElementBreak>
        </SectionBreak>
        <SectionBreak>
          <Grid container sx={{ marginBottom: '20px' }}>
            <Heading>Guarantee Purpose</Heading>
          </Grid>
          <ElementBreak>
            <Grid container sx={{ marginBottom: '10px' }} spacing={2}>
              <Grid item xs={3.94}>
                <Item>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    label="Contract Number"
                    placeholder="Enter Contract Number"
                    id="number"
                    name="number"
                    type="text"
                    autoComplete="off"
                    onChange={formik.handleChange('number')}
                    value={formik.values.number}
                    error={Boolean(touched.number && errors.number)}
                    helperText={touched.number && errors.number}
                    inputProps={{ maxLength: 1000 }}
                  />
                </Item>
              </Grid>
              <Grid item xs={1} />

              <Grid item xs={4.06}>
                <Item>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    label="Contract Name"
                    name="name"
                    id="name"
                    type="text"
                    autoComplete="off"
                    placeholder="Enter Contract Name"
                    value={formik?.values.name}
                    onChange={formik?.handleChange('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    inputProps={{ maxLength: 500 }}
                  />
                </Item>
              </Grid>
            </Grid>
          </ElementBreak>
          <Box>
            <ElementBreak>
              <Grid container item spacing={2}>
                <TimeRow
                  startDateValue={startDateValue}
                  endDateValue={endDateValue}
                  minDate={startDateValue}
                  maxDate={endDateValue}
                  setDateValue={handleDate}
                  openEnded={formik.values.isOpenEnded}
                  setOpenEnded={handleOpenEndedDate}
                  errors={errors}
                  touched={touched}
                  disablePast={false}
                />
              </Grid>
            </ElementBreak>
          </Box>
          <ElementBreak>
            <Grid item xs={9}>
              <TextField
                id="purpose-of-the-guarantee"
                multiline
                rows={10}
                placeholder="The detailed description of the contract."
                label="Purpose of the Guarantee"
                value={formik.values.purposeDescription}
                onChange={formik?.handleChange('purposeDescription')}
                size="medium"
                autoComplete="false"
                style={{ width: '100%' }}
                InputLabelProps={{ shrink: true }}
                error={Boolean(touched.purposeDescription && errors.purposeDescription)}
                helperText={touched.purposeDescription && errors.purposeDescription}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
          </ElementBreak>
          <Box sx={{ flexGrow: 1, ...classes }}>
            <Grid container item spacing={2}>
              <Grid item md={9} lg={9}>
                <TextField
                  id="description"
                  multiline
                  rows={10}
                  placeholder="Provide any additional comments here."
                  label="Optional Comments"
                  value={formik.values.comments}
                  onChange={formik?.handleChange('comments')}
                  size="medium"
                  autoComplete="false"
                  style={{ width: '100%' }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 1000 }}
                  error={Boolean(touched.comments && errors.comments)}
                  helperText={touched.comments && errors.comments}
                />
              </Grid>
            </Grid>
          </Box>
        </SectionBreak>
        <SectionBreak>
          <ElementBreak>
            <Heading>Guarantee Details</Heading>
          </ElementBreak>
          <ElementBreak>
            <ExpiryDate
              expiryDateValue={contractExpiryValue}
              setExpiryDate={handleDate}
              openEnded={formik.values.isOpenEnded}
              handleOpenEndedDate={handleOpenEndedDate}
              errors={errors}
              touched={touched}
            />
          </ElementBreak>
          <ElementBreak>
            <AmountAndCurrency
              setCurrency={setCurrency}
              amount={amountSet}
              setAmount={handleAmount}
              touched={touched}
              errors={errors}
            />
          </ElementBreak>
        </SectionBreak>
        <SectionBreak>
          <Grid item md={4} lg={4}>
            <Stack>
              <Grid sx={{ marginBottom: '1rem' }}>
                <Heading>Internal Reference Number</Heading>
              </Grid>
              <TextField
                label="IRN (Optional)"
                sx={{ maxWidth: '32%' }}
                autoComplete="off"
                type="text"
                InputLabelProps={{ shrink: true }}
                value={formik?.values?.reference}
                onChange={formik?.handleChange('reference')}
                inputProps={{ maxLength: 50 }}
              />
            </Stack>
          </Grid>
        </SectionBreak>
        <Box>
          <Grid container spacing={2}>
            <Grid container item spacing={1}>
              <Grid item md={1}>
                <Button sx={{ ...commonNoActionButtonStyle }} onClick={exitCreationProcess}>
                  Exit
                </Button>
              </Grid>
              {/* <Grid item md={1} lg={1} /> */}
              <Grid item md={4} lg={4}>
                <Button
                  variant="contained"
                  type="submit"
                  data-testid="review-and-submit-btn"
                  id="review-and-submit-btn"
                  sx={{ ...commonProceedButtonStyle }}
                >
                  Review and Submit
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </form>
    </div>
  )
}

export default PaperGuarantee
